import { useEffect, useLayoutEffect, useState } from 'react';
import { useBreadcrumbLabel } from '../../../contexts/BreadcrumbContext';
import { useChatSessions } from '../../../hooks/useChatSessions';
import { useProjects } from '../../../hooks/useProjects';
import {
  chatApi,
  documentApi,
  ragApi,
  ChatMessage,
  ChatSessionListItem,
  ContextItem,
  Document,
  Project,
  RAGCitation,
} from '../../../services/api';

interface UseChatSessionParams {
  initialSessionId: string | null;
  navigateToSession: (sessionId: string, options?: { replace?: boolean; state?: unknown }) => void;
  locationState: unknown;
  onMessagesLoaded: (messages: ChatMessage[]) => void;
  onSidebarCitationsChange: (citations: RAGCitation[]) => void;
  onSelectedDocsInit: (docs: Document[]) => void;
  onSelectedChatsInit: (chats: ChatSessionListItem[]) => void;
  onSelectedProjectsInit: (projects: Project[]) => void;
  onAttachedUploadIdsInit?: (ids: string[]) => void;
  onContextLockChange?: (locked: boolean) => void;
}

export function useChatSession({
  initialSessionId,
  navigateToSession,
  locationState,
  onMessagesLoaded,
  onSidebarCitationsChange,
  onSelectedDocsInit,
  onSelectedChatsInit,
  onSelectedProjectsInit,
  onAttachedUploadIdsInit,
  onContextLockChange,
}: UseChatSessionParams) {
  const { sessions: allSessions, refresh: refreshSessions } = useChatSessions();
  const { allProjects, loading: projectsLoading } = useProjects();
  const chatSessions = allSessions.slice(0, 10);

  const [sessionId, setSessionId] = useState<string | undefined>();
  const [sessionTitle, setSessionTitle] = useState<string>('');
  const [currentSessionRole, setCurrentSessionRole] = useState<'owner' | 'editor' | 'viewer' | null>(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(() => Boolean(initialSessionId));

  const { setBreadcrumbLabel } = useBreadcrumbLabel();

  useLayoutEffect(() => {
    if (!initialSessionId) {
      setSessionLoading(false);
      return;
    }
    setSessionLoading(true);
    onMessagesLoaded([]);
    onSidebarCitationsChange([]);
  }, [initialSessionId, onMessagesLoaded, onSidebarCitationsChange]);

  const loadSession = async (id: string) => {
    try {
      const [messagesResult, savedContext] = await Promise.all([
        chatApi.getMessages(id),
        ragApi.getSessionContext(id).catch(() => null),
      ]);
      const { messages } = messagesResult;
      onMessagesLoaded(messages);
      setSessionId(id);

      const withCitations = [...messages].reverse().find(
        (m) => m.role === 'assistant' && m.citations && m.citations.length > 0
      );
      onSidebarCitationsChange(withCitations?.citations ?? []);

      const currentSession = allSessions.find((s) => s.id === id);
      setCurrentSessionRole(currentSession?.role || 'owner');
      setSessionTitle(currentSession?.title ?? 'Conversation');

      if (onContextLockChange) {
        onContextLockChange(true);
      }

      if (savedContext) {
        const projectIds = savedContext.projects
          .map((p) => p.project_id)
          .filter(Boolean) as string[];
        if (projectIds.length > 0) {
          const selected = allProjects.filter((p) => projectIds.includes(p.id));
          if (selected.length > 0) onSelectedProjectsInit(selected);
        }

        const docIds = savedContext.documents
          .map((d) => d.document_id)
          .filter(Boolean) as string[];
        if (docIds.length > 0) {
          const docs = await Promise.all(
            docIds.map((docId) => documentApi.getDocument(docId).catch(() => null))
          );
          const validDocs = docs.filter((d): d is Document => d !== null);
          if (validDocs.length > 0) onSelectedDocsInit(validDocs);
        }

        const chatIds = savedContext.previous_chats
          ?.map((c) => c.referenced_session_id)
          .filter(Boolean) as string[] | undefined;
        if (chatIds && chatIds.length > 0) {
          const selectedChats = allSessions.filter((s) => chatIds.includes(s.id));
          if (selectedChats.length === 0) {
            const fallback = chatIds.map((chatId) => {
              const fromContext = savedContext.previous_chats?.find(
                (c) => c.referenced_session_id === chatId,
              );
              return {
                id: chatId,
                title: fromContext?.title ?? 'Previous chat',
                created_at: new Date().toISOString(),
                updated_at: null,
                is_owner: true,
                role: 'owner' as const,
              };
            });
            onSelectedChatsInit(fallback);
          } else {
            onSelectedChatsInit(selectedChats);
          }
        }
      }
    } catch (error) {
      console.error('Error loading session:', error);
    } finally {
      setSessionLoading(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      if (initialSessionId) {
        await loadSession(initialSessionId);
        setInitialDataLoaded(true);
        return;
      }

      const pendingContext = sessionStorage.getItem('pendingContextItems');
      if (!pendingContext) {
        setInitialDataLoaded(true);
        return;
      }

      try {
        const contextItems: ContextItem[] = JSON.parse(pendingContext);
        const chatIds = contextItems
          .filter((item) => item.context_type === 'previous_chat' && item.referenced_session_id)
          .map((item) => item.referenced_session_id!);

        if (chatIds.length > 0 && allSessions.length === 0) {
          return;
        }

        const projectIds = contextItems
          .filter((item) => item.context_type === 'project' && (item as ContextItem & { project_id?: string }).project_id)
          .map((item) => (item as ContextItem & { project_id: string }).project_id);

        if (projectIds.length > 0 && allProjects.length === 0 && projectsLoading) {
          return;
        }

        const docIds = contextItems
          .filter((item) => item.context_type === 'document' && item.document_id)
          .map((item) => item.document_id!);
        if (docIds.length > 0) {
          const docs = await Promise.all(
            docIds.map((id) => documentApi.getDocument(id).catch(() => null))
          );
          onSelectedDocsInit(docs.filter((d) => d !== null) as Document[]);
        }

        if (chatIds.length > 0) {
          onSelectedChatsInit(allSessions.filter((s) => chatIds.includes(s.id)));
        }

        if (projectIds.length > 0) {
          const selected = allProjects.filter((p) => projectIds.includes(p.id));
          if (selected.length > 0) {
            onSelectedProjectsInit(selected);
          }
        }

        const uploadDocIds = contextItems
          .filter((item) => item.context_type === 'upload' && item.document_id)
          .map((item) => item.document_id!);
        if (uploadDocIds.length > 0) {
          onAttachedUploadIdsInit?.(uploadDocIds);
        }

        sessionStorage.removeItem('pendingContextItems');
      } catch (e) {
        console.error('Failed to parse pending context items', e);
      }

      setInitialDataLoaded(true);
    };

    void loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSessionId, allSessions, allProjects, projectsLoading]);

  useEffect(() => {
    if (sessionId && allSessions.length > 0) {
      const session = allSessions.find((s) => s.id === sessionId);
      if (session?.title) setSessionTitle(session.title);
      if (session?.role) setCurrentSessionRole(session.role);
    }
  }, [sessionId, allSessions]);

  useEffect(() => {
    if (sessionId) {
      setBreadcrumbLabel(sessionTitle || 'Conversation');
    } else {
      setBreadcrumbLabel(null);
    }
    return () => setBreadcrumbLabel(null);
  }, [sessionId, sessionTitle, setBreadcrumbLabel]);

  return {
    sessionId,
    setSessionId,
    sessionTitle,
    setSessionTitle,
    chatSessions,
    currentSessionRole,
    setCurrentSessionRole,
    initialDataLoaded,
    sessionLoading,
    loadSession,
    refreshSessions,
  };
}
